import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import type {
  CalendarEventGroup,
  CalendarEventGroupAnchorMode,
  DelegateCalendarSummary,
  ExecutiveCalendarSettings,
  SavedCalendarPlace,
} from '../../types';
import {
  activitySegmentIndex,
  buildTripSegments,
  computeTravelDurations,
  groupSegmentsToPlanned,
  plannedToSyncPayload,
  scheduleSegments,
  shiftScheduledSegments,
  type CalendarActivityStop,
  type PlannedSegment,
  formatSegmentDurationLabel,
} from '../../utils/calendarEventGroupPlan';
import { getDrivingDuration, clearDrivingDurationCache } from '../../utils/calendarTravelTime';
import {
  executiveCalendarDeleteEvent,
  executiveCalendarDeleteEventGroup,
  executiveCalendarInsertEvent,
  executiveCalendarInsertEventGroup,
  executiveCalendarPatchEvent,
  executiveCalendarSyncEventGroup,
  getEventGroupId,
  type DelegateCalendarEventRow,
} from '../../utils/executiveCalendarDelegatedService';
import {
  getHomePlace,
  newSavedPlaceId,
  resolvePrimaryCalendarId,
  resolveTravelCalendarId,
} from '../../utils/executiveCalendarSettings';
import { findGroupByGoogleEventId, newEventGroupId } from '../../utils/executiveCalendarEventGroups';
import {
  parseLocalDateAndTime,
  toGoogleCalendarDateTime,
} from '../../utils/googleCalendarDateTime';
import {
  buildConflictResolutionPlan,
  defaultConflictActions,
  findTripConflictUnits,
  getTripBounds,
  type ConflictResolutionAction,
} from '../../utils/calendarTripConflicts';
import {
  parseTripInviteeInput,
} from '../../utils/tripInvitees';
import TripPlacePicker from './TripPlacePicker';
import TripConflictResolutionPanel from './TripConflictResolutionPanel';
import './TripConflictResolutionPanel.css';
import './EventBuilderModal.css';

type Props = {
  executiveUid: string;
  settings: ExecutiveCalendarSettings;
  calendars: DelegateCalendarSummary[];
  writableCalendarIds: string[];
  existingEvents: DelegateCalendarEventRow[];
  eventGroups?: CalendarEventGroup[];
  timeZone: string;
  initialDay: Date;
  initialStartMin: number;
  onClose: () => void;
  onCreated: () => void;
};

type ActivityRow = {
  id: string;
  placeId: string;
  title: string;
  durationMinutes: number;
  calendarId: string;
};

function localTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const EventBuilderModal: React.FC<Props> = ({
  executiveUid,
  settings,
  calendars,
  writableCalendarIds,
  existingEvents,
  eventGroups = [],
  timeZone,
  initialDay,
  initialStartMin,
  onClose,
  onCreated,
}) => {
  const linkedEventGroups = eventGroups ?? [];
  const home = getHomePlace(settings);
  const travelCalId = resolveTravelCalendarId(settings, calendars, writableCalendarIds);
  const primaryCalId = resolvePrimaryCalendarId(calendars, writableCalendarIds);

  const [anchorMode, setAnchorMode] = useState<CalendarEventGroupAnchorMode>('activity_start');
  const [anchorActivityIndex, setAnchorActivityIndex] = useState(0);
  const [anchorDate, setAnchorDate] = useState(() => format(initialDay, 'yyyy-MM-dd'));
  const [anchorTime, setAnchorTime] = useState(() => {
    const h = Math.floor(initialStartMin / 60);
    const m = initialStartMin % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  });

  const [activities, setActivities] = useState<ActivityRow[]>(() => {
    const gymPlace = settings.savedPlaces.find(p => /gym/i.test(p.label));
    return [
      {
        id: 'act-1',
        placeId: gymPlace?.id || settings.savedPlaces.find(p => p.id !== settings.homePlaceId)?.id || '',
        title: 'Gym',
        durationMinutes: 60,
        calendarId: primaryCalId || '',
      },
    ];
  });

  const [segments, setSegments] = useState<PlannedSegment[]>([]);
  const [travelWarnings, setTravelWarnings] = useState<string[]>([]);
  const [computing, setComputing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictStep, setConflictStep] = useState(false);
  const [conflictActions, setConflictActions] = useState<Record<string, ConflictResolutionAction>>({});
  const [tripPlaces, setTripPlaces] = useState<SavedCalendarPlace[]>(() => [...settings.savedPlaces]);
  const [inviteeInput, setInviteeInput] = useState('');

  const tripInvitees = useMemo(() => parseTripInviteeInput(inviteeInput), [inviteeInput]);

  useEffect(() => {
    setTripPlaces(prev => {
      const byId = new Map(prev.map(p => [p.id, p]));
      for (const p of settings.savedPlaces) {
        if (!byId.has(p.id)) byId.set(p.id, p);
      }
      return Array.from(byId.values());
    });
  }, [settings.savedPlaces]);

  const placesById = useMemo(() => {
    const map = new Map<string, SavedCalendarPlace>();
    for (const p of tripPlaces) map.set(p.id, p);
    return map;
  }, [tripPlaces]);

  const resolvePlaceLabel = useCallback(
    (placeId: string) => placesById.get(placeId)?.label || '?',
    [placesById],
  );

  const calendarById = useMemo(() => new Map(calendars.map(c => [c.id, c])), [calendars]);

  const previewCalendarStyle = useCallback(
    (calendarId: string, segmentType: 'travel' | 'activity') => {
      const cal = calendarById.get(calendarId);
      const bg =
        cal?.backgroundColor || (segmentType === 'travel' ? '#fef3c7' : '#dbeafe');
      return {
        background: `${bg}cc`,
        borderLeft: `3px solid ${bg}`,
      } as const;
    },
    [calendarById],
  );

  const endPlace = home;

  const conflictUnits = useMemo(
    () =>
      findTripConflictUnits(segments, existingEvents, {
        writableCalendarIds,
        getGroupId: getEventGroupId,
      }),
    [segments, existingEvents, writableCalendarIds],
  );

  const tripBounds = useMemo(() => getTripBounds(segments), [segments]);

  const applyConflictResolutions = async (actions: Record<string, ConflictResolutionAction>) => {
    if (!tripBounds) return;

    const units = findTripConflictUnits(segments, existingEvents, {
      writableCalendarIds,
      getGroupId: getEventGroupId,
    });
    const plan = buildConflictResolutionPlan(units, actions, tripBounds.end, segments);
    const tz = timeZone || localTz();
    const errors: string[] = [];

    for (const op of plan.deletes) {
      try {
        if (op.unit.groupId) {
          await executiveCalendarDeleteEventGroup({
            executiveUid,
            groupId: op.unit.groupId,
          });
        } else {
          const ev = op.unit.events[0];
          await executiveCalendarDeleteEvent({
            executiveUid,
            calendarId: ev.calendarId,
            eventId: ev.id,
          });
        }
      } catch (e) {
        errors.push(
          `Could not delete ${op.unit.title}: ${e instanceof Error ? e.message : 'unknown error'}`,
        );
      }
    }

    for (const op of plan.adjusts) {
      try {
        if (op.unit.kind === 'group' && op.unit.groupId) {
          const anchorEventId = op.unit.events[0]?.id;
          const group = anchorEventId
            ? findGroupByGoogleEventId(linkedEventGroups, anchorEventId)
            : undefined;
          if (!group?.id) {
            throw new Error('linked trip metadata not found');
          }
          const planned = groupSegmentsToPlanned(group);
          const deltaMs = op.start.getTime() - op.unit.start.getTime();
          const shifted = shiftScheduledSegments(planned, deltaMs);
          await executiveCalendarSyncEventGroup({
            executiveUid,
            groupId: group.id,
            timeZone: tz,
            segments: plannedToSyncPayload(shifted),
          });
        } else {
          const ev = op.unit.events[0];
          await executiveCalendarPatchEvent({
            executiveUid,
            calendarId: ev.calendarId,
            eventId: ev.id,
            resource: {
              start: { dateTime: toGoogleCalendarDateTime(op.start), timeZone: tz },
              end: { dateTime: toGoogleCalendarDateTime(op.end), timeZone: tz },
            },
          });
        }
      } catch (e) {
        errors.push(
          `Could not move ${op.unit.title}: ${e instanceof Error ? e.message : 'unknown error'}`,
        );
      }
    }

    for (const op of plan.cutouts) {
      try {
        const ev = op.unit.events[0];
        if (op.ranges.length === 0) {
          await executiveCalendarDeleteEvent({
            executiveUid,
            calendarId: ev.calendarId,
            eventId: ev.id,
          });
          continue;
        }

        const [first, ...rest] = op.ranges;
        await executiveCalendarPatchEvent({
          executiveUid,
          calendarId: ev.calendarId,
          eventId: ev.id,
          resource: {
            start: { dateTime: toGoogleCalendarDateTime(first.start), timeZone: tz },
            end: { dateTime: toGoogleCalendarDateTime(first.end), timeZone: tz },
          },
        });

        for (const piece of rest) {
          await executiveCalendarInsertEvent({
            executiveUid,
            calendarId: ev.calendarId,
            body: {
              summary: ev.title,
              description: ev.description,
              location: ev.location,
              start: { dateTime: toGoogleCalendarDateTime(piece.start), timeZone: tz },
              end: { dateTime: toGoogleCalendarDateTime(piece.end), timeZone: tz },
            },
          });
        }
      } catch (e) {
        errors.push(
          `Could not cut out ${op.unit.title}: ${e instanceof Error ? e.message : 'unknown error'}`,
        );
      }
    }

    if (errors.length > 0) {
      throw new Error(errors.join(' '));
    }
  };

  const executeCreate = async (actions: Record<string, ConflictResolutionAction>) => {
    if (!home || !travelCalId || segments.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const groupId = newEventGroupId();
      const anchorSegIdx = activitySegmentIndex(segments, anchorActivityIndex);
      await executiveCalendarInsertEventGroup({
        executiveUid,
        groupId,
        anchorMode,
        anchorSegmentIndex: anchorSegIdx >= 0 ? anchorSegIdx : 1,
        timeZone: timeZone || localTz(),
        invitees: tripInvitees,
        segments: segments.map(seg => ({
          segmentId: seg.segmentId,
          type: seg.type,
          calendarId: seg.calendarId,
          title: seg.title,
          location: seg.location,
          fromPlaceId: seg.fromPlaceId,
          toPlaceId: seg.toPlaceId,
          durationMinutes: seg.durationMinutes,
          travelDurationSeconds: seg.travelDurationSeconds,
          start: toGoogleCalendarDateTime(seg.start!),
          end: toGoogleCalendarDateTime(seg.end!),
        })),
      });

      if (conflictUnits.some(u => u.canModify)) {
        await applyConflictResolutions(actions);
      }

      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create trip');
    } finally {
      setCreating(false);
    }
  };

  const handleCreate = () => {
    if (!home || !travelCalId || segments.length === 0) return;
    if (conflictUnits.length > 0 && !conflictStep) {
      setConflictActions(defaultConflictActions(conflictUnits, segments));
      setConflictStep(true);
      return;
    }
    void executeCreate(conflictActions);
  };

  const handleConflictActionChange = (key: string, action: ConflictResolutionAction) => {
    setConflictActions(prev => ({ ...prev, [key]: action }));
  };

  const recompute = useCallback(async () => {
    if (!home || !travelCalId || !endPlace) {
      setSegments([]);
      return;
    }

    const validActivities: CalendarActivityStop[] = activities
      .filter(a => a.placeId && a.title.trim())
      .map(a => ({
        id: a.id,
        placeId: a.placeId,
        title: a.title.trim(),
        durationMinutes: a.durationMinutes,
        calendarId: a.calendarId || primaryCalId || travelCalId,
      }));

    if (validActivities.length === 0) {
      setSegments([]);
      return;
    }

    setComputing(true);
    setError(null);
    clearDrivingDurationCache();

    try {
      let planned = buildTripSegments(home, endPlace, validActivities, placesById, travelCalId);
      const { segments: withTravel, warnings } = await computeTravelDurations(planned, input =>
        getDrivingDuration(executiveUid, input),
      );
      planned = withTravel;
      setTravelWarnings(warnings);

      const anchorDateTime = parseLocalDateAndTime(anchorDate, anchorTime);

      const anchorSegIdx = activitySegmentIndex(planned, anchorActivityIndex);
      const scheduled = scheduleSegments(
        planned,
        anchorMode,
        anchorDateTime,
        anchorSegIdx >= 0 ? anchorSegIdx : 1,
      );
      setSegments(scheduled);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not compute trip');
      setTravelWarnings([]);
      setSegments([]);
    } finally {
      setComputing(false);
    }
  }, [
    home,
    endPlace,
    travelCalId,
    primaryCalId,
    activities,
    placesById,
    anchorMode,
    anchorActivityIndex,
    anchorDate,
    anchorTime,
    executiveUid,
  ]);

  useEffect(() => {
    setConflictStep(false);
  }, [segments, anchorDate, anchorTime, anchorMode, activities]);

  useEffect(() => {
    const t = setTimeout(() => void recompute(), 300);
    return () => clearTimeout(t);
  }, [recompute]);

  const applyGymTemplate = () => {
    const gymPlace = settings.savedPlaces.find(p => /gym/i.test(p.label));
    if (!gymPlace || !primaryCalId) return;
    setActivities([
      {
        id: newSavedPlaceId(),
        placeId: gymPlace.id,
        title: 'Gym',
        durationMinutes: 60,
        calendarId: primaryCalId,
      },
    ]);
    setAnchorActivityIndex(0);
    setAnchorMode('activity_start');
  };

  const addActivity = () => {
    const other = settings.savedPlaces.find(p => p.id !== settings.homePlaceId);
    setActivities(prev => [
      ...prev,
      {
        id: newSavedPlaceId(),
        placeId: other?.id || '',
        title: 'Meeting',
        durationMinutes: 60,
        calendarId: primaryCalId || '',
      },
    ]);
  };

  const removeActivity = (id: string) => {
    setActivities(prev => (prev.length <= 1 ? prev : prev.filter(a => a.id !== id)));
  };

  const updateActivity = (id: string, patch: Partial<ActivityRow>) => {
    setActivities(prev => prev.map(a => (a.id === id ? { ...a, ...patch } : a)));
  };

  if (!home) {
    return (
      <div className="eac-modal-overlay" role="dialog" aria-modal>
        <div className="eeb-modal">
          <h3>Build trip</h3>
          <p className="eeb-hint">
            Set a Home place in Calendar settings before building trips.
          </p>
          <div className="eac-modal-actions">
            <button type="button" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="eac-modal-overlay"
      role="dialog"
      aria-modal
      onMouseDown={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="eeb-modal">
        <h3>Build trip</h3>

        <div className="eeb-templates">
          <button type="button" className="eac-template-btn" onClick={applyGymTemplate}>
            Gym template
          </button>
        </div>

        <div className="eeb-anchor-row">
          <div className="eac-field">
            <label>Schedule by</label>
            <select
              value={anchorMode}
              onChange={e => setAnchorMode(e.target.value as CalendarEventGroupAnchorMode)}
            >
              <option value="activity_start">Activity starts at</option>
              <option value="leave_home">Leave Home at</option>
            </select>
          </div>
          <div className="eac-field">
            <label>{anchorMode === 'leave_home' ? 'Leave Home' : 'Anchor activity'}</label>
            {anchorMode === 'activity_start' && activities.length > 1 && (
              <select
                value={anchorActivityIndex}
                onChange={e => setAnchorActivityIndex(Number(e.target.value))}
                style={{ marginBottom: 6 }}
              >
                {activities.map((a, i) => (
                  <option key={a.id} value={i}>
                    {a.title || `Stop ${i + 1}`}
                  </option>
                ))}
              </select>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input type="date" value={anchorDate} onChange={e => setAnchorDate(e.target.value)} />
              <input type="time" value={anchorTime} onChange={e => setAnchorTime(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="eeb-invitees">
          <h4>Invitees</h4>
          <p className="eeb-hint">
            Email addresses are added to every trip event (travel and activities). Google Calendar
            invitations are sent when the trip is created.
          </p>
          <textarea
            className="eeb-invitee-input"
            value={inviteeInput}
            onChange={e => setInviteeInput(e.target.value)}
            placeholder="name@company.com, teammate@example.com"
            rows={2}
          />
          {tripInvitees.length > 0 && (
            <div className="eeb-invitee-chips" aria-label="Parsed invitees">
              {tripInvitees.map(email => (
                <span key={email} className="eeb-invitee-chip">
                  {email}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="eeb-stops">
          <div className="eeb-stops-head">
            <h4>Stops</h4>
            <button type="button" className="eeb-add-stop" onClick={addActivity}>
              + Add stop
            </button>
          </div>
          <p className="eeb-route-hint">
            Route: {home.label} → {activities.map(a => resolvePlaceLabel(a.placeId)).join(' → ')} → {home.label}
          </p>

          {activities.map((act, idx) => (
            <div key={act.id} className="eeb-activity-row">
              <div className="eeb-activity-num">{idx + 1}</div>
              <div className="eeb-activity-fields">
                <input
                  value={act.title}
                  onChange={e => updateActivity(act.id, { title: e.target.value })}
                  placeholder="Activity title"
                />
                <TripPlacePicker
                  placeId={act.placeId}
                  tripPlaces={tripPlaces}
                  onSelect={id => updateActivity(act.id, { placeId: id })}
                  onAddPlace={place => setTripPlaces(prev => [...prev, place])}
                />
                <div className="eeb-activity-meta">
                  <label>
                    Duration (min)
                    <input
                      type="number"
                      min={5}
                      step={5}
                      value={act.durationMinutes}
                      onChange={e =>
                        updateActivity(act.id, { durationMinutes: Number(e.target.value) })
                      }
                    />
                  </label>
                  <label>
                    Calendar
                    <select
                      value={act.calendarId}
                      onChange={e => updateActivity(act.id, { calendarId: e.target.value })}
                    >
                      {calendars
                        .filter(c => writableCalendarIds.includes(c.id))
                        .map(c => (
                          <option key={c.id} value={c.id}>
                            {c.summary || c.id}
                          </option>
                        ))}
                    </select>
                  </label>
                </div>
              </div>
              {activities.length > 1 && (
                <button type="button" className="eeb-remove" onClick={() => removeActivity(act.id)}>
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="eeb-preview">
          <h4>Preview {computing && <span className="eeb-computing">Calculating travel…</span>}</h4>
          {travelWarnings.length > 0 && (
            <ul className="eeb-travel-warnings">
              {travelWarnings.map(w => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          {segments.length === 0 ? (
            <p className="eeb-preview-empty">Add stops and places to see the timeline.</p>
          ) : (
            <ul className="eeb-preview-list">
              {segments.map(seg => {
                const cal = calendarById.get(seg.calendarId);
                return (
                <li
                  key={seg.segmentId}
                  className="eeb-preview-seg"
                  style={previewCalendarStyle(seg.calendarId, seg.type)}
                  title={cal?.summary ? `${cal.summary} calendar` : undefined}
                >
                  <span className="eeb-preview-type">{seg.type === 'travel' ? 'Travel' : 'Activity'}</span>
                  <span className="eeb-preview-title">{seg.title}</span>
                  <span className="eeb-preview-time">
                    {seg.start && seg.end
                      ? `${format(seg.start, 'h:mm a')} – ${format(seg.end, 'h:mm a')} (${formatSegmentDurationLabel(seg)})`
                      : ''}
                  </span>
                </li>
                );
              })}
            </ul>
          )}
        </div>

        {conflictUnits.length > 0 && !conflictStep && (
          <div className="eeb-conflicts-preview">
            <h4>
              {conflictUnits.length} scheduling conflict{conflictUnits.length === 1 ? '' : 's'}
            </h4>
            <ul className="eeb-conflicts-preview-list">
              {conflictUnits.map(unit => (
                <li key={unit.key}>
                  <span className="eeb-conflicts-preview-title">{unit.title}</span>
                  <span className="eeb-conflicts-preview-time">
                    {format(unit.start, 'h:mm a')} – {format(unit.end, 'h:mm a')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && <div className="eac-banner err">{error}</div>}

        {conflictStep && tripBounds ? (
          <TripConflictResolutionPanel
            units={conflictUnits}
            tripEnd={tripBounds.end}
            travelSegments={segments}
            actions={conflictActions}
            onActionChange={handleConflictActionChange}
            onBack={() => setConflictStep(false)}
            onConfirm={() => void executeCreate(conflictActions)}
            confirming={creating}
          />
        ) : (
          <div className="eac-modal-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="primary"
              disabled={creating || computing || segments.length === 0}
              onClick={handleCreate}
            >
              {creating
                ? 'Creating…'
                : conflictUnits.length > 0
                  ? `Review ${conflictUnits.length} conflict${conflictUnits.length === 1 ? '' : 's'} & create`
                  : `Create ${segments.length} events`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventBuilderModal;
